import React, { useCallback, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useForm, useWatch } from 'react-hook-form'
import { fetchRestaurantHighlightRules, updateRestaurantHighlightSetting } from '@/apis/aipass'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { EditFooter } from '@/components/organisms/edit-footer'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { HighlightSettingPane } from '@/components/organisms/settings/restaurant/highlight-setting-pane'
import { SettingRestaurantTab } from '@/components/organisms/settings/restaurant/tab'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'
import type { RestaurantHighlightRules } from '@/models/restaurant-manager'

export const SettingRestaurantHighlight: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { isSettingsReadOnly, guardSettingsEdit } = useSettingsEditGuard()
  const [loading, setLoading] = useState(true)
  const [snapshot, setSnapshot] = useState<RestaurantHighlightRules | null>(null)
  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    setValue,
  } = useForm<RestaurantHighlightRules>({
    defaultValues: {
      checkinDays: 0,
      updatedDays: 0,
    },
  })
  const highlightValue = useWatch({ control }) ?? { checkinDays: 0, updatedDays: 0 }

  const initializeComponent = useCallback(async () => {
    try {
      setLoading(true)
      const highlightRules = await fetchRestaurantHighlightRules()
      setSnapshot(highlightRules)
      reset(highlightRules)
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  }, [errorHandler, reset])

  useEffect(() => {
    initializeComponent()
  }, [initializeComponent])
  const saveHighlightSetting = handleSubmit(async value => {
    if (!guardSettingsEdit()) return
    try {
      setLoading(true)
      await updateRestaurantHighlightSetting(value)
      await initializeComponent()
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  })

  const cancelHighlightSetting = useCallback(() => {
    if (!snapshot) {
      return
    }

    reset(snapshot)
  }, [reset, snapshot])

  const updateHighlightValue = useCallback(
    (field: keyof RestaurantHighlightRules, nextValue: number) => {
      if (!guardSettingsEdit()) return
      setValue(field, nextValue, { shouldDirty: true })
    },
    [guardSettingsEdit, setValue],
  )

  const footerContent = isDirty ? <EditFooter onSave={saveHighlightSetting} onCancel={cancelHighlightSetting} /> : undefined

  return (
    <SettingsLayout loading={loading} footerContent={footerContent}>
      <TabContainer tabComponent={<SettingRestaurantTab currentTab="RestaurantManager.HighlightSettings" />} isShowFooter={isDirty}>
        {snapshot ? (
          <HighlightSettingPane
            value={{
              checkinDays: highlightValue.checkinDays ?? 0,
              updatedDays: highlightValue.updatedDays ?? 0,
            }}
            disabled={isSettingsReadOnly}
            onChange={updateHighlightValue}
          />
        ) : (
          <div css={statusStyle}>{t('No data available')}</div>
        )}
      </TabContainer>
    </SettingsLayout>
  )
}

const statusStyle = css({
  alignItems: 'center',
  color: '#676767',
  display: 'flex',
  fontSize: 18,
  justifyContent: 'center',
  minHeight: '240px',
  padding: 32,
})
